import { defineStore } from 'pinia';
import { arrancaElGrabador } from '@/services/grabador';
import { sinIdioma } from '@/i18n/idioma';

// 🔴 **Los códigos de verdad, y aquí es donde se cargan** (22.09.26).
//
// Hasta hoy esto decía `G-XXXXXXXXXX`, el ejemplo que viene de fábrica, con un
// «TODO» al lado: el interruptor de «Cookies Analíticas» encendía una medición
// que no existía. Mientras tanto, `index.html` cargaba las etiquetas buenas en
// toda visita, antes de aceptar nada. Quien pulsaba «Solo las necesarias»
// creía haber dicho que no y Google le medía igual.
//
// Encargo de Pablo: «hacemos lo correcto». Las dos etiquetas se cargan aquí y
// solo con la analítica aceptada. `GT-` es la de Google Analytics y `AW-` la de
// conversiones de Google Ads, que es la que de verdad pide consentimiento.
const GTAG_ID = 'GT-NGJ3BHGK';
const GOOGLE_ADS_ID = 'AW-1036729618';
const CONSENT_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000; // 1 año

export const useConsentStore = defineStore('consent', {
  state: () => ({
    cookiesAccepted: false,
    analyticsEnabled: false,
    cookieConsentDate: null,
    showCookieBanner: false,
  }),

  getters: {
    hasValidConsent(state) {
      if (!state.cookieConsentDate) return false;

      const consentAge = Date.now() - new Date(state.cookieConsentDate).getTime();
      return consentAge < CONSENT_EXPIRY_MS;
    },
  },

  actions: {
    initFromLocalStorage() {
      try {
        const consent = localStorage.getItem('astra_consent');

        if (consent) {
          const data = JSON.parse(consent);
          this.cookiesAccepted = data.cookiesAccepted || false;
          this.analyticsEnabled = data.analyticsEnabled || false;
          this.cookieConsentDate = data.cookieConsentDate || null;

          // Verificar si el consentimiento expiró
          if (!this.hasValidConsent) {
            this.resetConsent();
            this.showCookieBanner = true;
          } else if (this.analyticsEnabled) {
            this.loadGoogleAnalytics();
            this.arrancarGrabador();
          }
        } else {
          // Primera visita, mostrar banner
          this.showCookieBanner = true;
        }
      } catch (error) {
        console.error('Error loading consent from localStorage:', error);
        this.showCookieBanner = true;
      }
    },

    saveToLocalStorage() {
      try {
        const data = {
          cookiesAccepted: this.cookiesAccepted,
          analyticsEnabled: this.analyticsEnabled,
          cookieConsentDate: this.cookieConsentDate,
        };
        localStorage.setItem('astra_consent', JSON.stringify(data));
      } catch (error) {
        console.error('Error saving consent to localStorage:', error);
      }
    },

    acceptAllCookies() {
      this.cookiesAccepted = true;
      this.analyticsEnabled = true;
      this.cookieConsentDate = new Date().toISOString();
      this.showCookieBanner = false;

      this.saveToLocalStorage();
      this.loadGoogleAnalytics();
      this.arrancarGrabador();
    },

    acceptNecessaryCookies() {
      this.cookiesAccepted = true;
      this.analyticsEnabled = false;
      this.cookieConsentDate = new Date().toISOString();
      this.showCookieBanner = false;

      this.saveToLocalStorage();
      this.removeGoogleAnalytics();
    },

    configureCookies(analyticsEnabled) {
      this.cookiesAccepted = true;
      this.analyticsEnabled = analyticsEnabled;
      this.cookieConsentDate = new Date().toISOString();
      this.showCookieBanner = false;

      this.saveToLocalStorage();

      if (analyticsEnabled) {
        this.loadGoogleAnalytics();
        this.arrancarGrabador();
      } else {
        this.removeGoogleAnalytics();
      }
    },

    /**
     * Arranca el grabador de Umami, el que llena los mapas de calor.
     *
     * 🔴 **Solo con la analítica aceptada.** No es medición esencial: registra
     * clics y desplazamiento, así que va detrás del consentimiento como el
     * resto. Contrato `PETICION_HEATMAPS_Y_PRIVACIDAD_WEB.md`, 22.09.26.
     *
     * ⚠️ **No se apaga al retirar el consentimiento**, y no es un descuido:
     * el script no se puede detener una vez cargado. Quien lo retira dejará de
     * grabarse en cuanto recargue, y mientras tanto no se le abre ninguna
     * sesión nueva. Por eso tampoco se arranca desde `resetConsent`.
     */
    arrancarGrabador() {
      if (!this.analyticsEnabled) return;
      if (typeof window === 'undefined') return;
      arrancaElGrabador(sinIdioma(window.location.pathname));
    },

    resetConsent() {
      this.cookiesAccepted = false;
      this.analyticsEnabled = false;
      this.cookieConsentDate = null;

      localStorage.removeItem('astra_consent');
      this.removeGoogleAnalytics();
    },

    loadGoogleAnalytics() {
      if (!this.analyticsEnabled) return;

      try {
        // Verificar si ya existe
        if (window.gtag) return;

        // Crear script de Google Analytics
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
        document.head.appendChild(script);

        // Inicializar dataLayer
        window.dataLayer = window.dataLayer || [];
        const gtag = function() {
          window.dataLayer.push(arguments);
        };
        window.gtag = gtag;

        gtag('js', new Date());
        // Las dos, como estaban en `index.html`: sin esta segunda, las campañas
        // dejarían de medir conversiones sin que nadie lo pidiera.
        gtag('config', GTAG_ID);
        gtag('config', GOOGLE_ADS_ID);
      } catch (error) {
        console.error('Error loading Google Analytics:', error);
      }
    },

    removeGoogleAnalytics() {
      try {
        // Eliminar cookies de Google Analytics
        const cookiesToRemove = ['_ga', '_gid', '_gat'];
        cookiesToRemove.forEach(cookieName => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        });

        // Limpiar dataLayer si existe
        if (window.dataLayer) {
          window.dataLayer = [];
        }

        // Eliminar gtag
        if (window.gtag) {
          delete window.gtag;
        }

      } catch (error) {
        console.error('Error removing Google Analytics:', error);
      }
    },
  },
});
